/**
 * ImagesPanel — right-rail image management.
 *
 * S-072 / FEAT-006 (Phase 9). Swapped from Directus-backed
 * `useSetSelectedImage` + `assetUrl` to Hono /images/:id (PATCH) +
 * the R2 asset proxy. Image rows are rendered as PieceImage (adapted
 * from HonoPieceImage by the PieceView edge); the legacy ArticleImage
 * name was retired in S-086.
 *
 * "Mark as featured/social" toggles one row to true and the rest to
 * false. The Hono /images/:id PATCH accepts selected_featured /
 * selected_social independently; we issue one PATCH per row to keep
 * the implementation symmetric. Two requests per click is fine at
 * v1 (typical pieces have ≤6 images).
 *
 * S-174 / FEAT-019 — added ImageUploader: drag-drop zone + file picker.
 * Two-step upload: POST /files (multipart) → POST /pieces/:id/images
 * with style:"stock" and format:"landscape". Invalidates ["piece-images",
 * pieceId] on success so the panel refreshes without a page reload.
 */
import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { PieceImage } from "@/types/piece";
import { r2AssetUrl } from "@/lib/asset-url";
import { formatContentType } from "@/lib/format";
import { API_BASE_URL, useApi } from "@/lib/api";
import { useAuth } from "@clerk/clerk-react";
import { classifyImageFormat } from "@/lib/image-format";
import { useRole } from "@/lib/useRole";

// Accepted image MIME types (matching the server's /files endpoint).
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
// Warn if the file exceeds 5 MB (server enforces its own limit).
const WARN_SIZE_BYTES = 5 * 1024 * 1024;

interface Props {
  images: PieceImage[];
  pieceId: number;
  /**
   * FEAT-087 — in the side column a thumbnail opens the image at full size
   * in the main window. Marking an image featured/social stays available as
   * its own control on the tile, because one click cannot do both and
   * dropping either would lose real function.
   */
  onOpenImage?: (id: number) => void;
  openImageId?: number | null;
}

export function ImagesPanel({ images, pieceId, onOpenImage, openImageId }: Props) {
  const landscape = images.filter((i) => classifyImageFormat(i.format) === "featured");
  const square = images.filter((i) => classifyImageFormat(i.format) === "social");
  const pressKit = images.filter((i) => classifyImageFormat(i.format) === "press_kit");
  const other = images.filter((i) => classifyImageFormat(i.format) === "other");
  const { canEdit } = useRole();

  return (
    <div className="panel-body">
      <div className="panel-header">
        <h3>Images</h3>
        <span className="panel-sub">
          {images.length} image{images.length === 1 ? "" : "s"}
          {onOpenImage ? " · click to view" : " · click to mark featured / social"}
        </span>
      </div>

      {canEdit && <ImageUploader pieceId={pieceId} />}

      {landscape.length > 0 && (
        <>
          <div className="panel-section-label">Featured</div>
          <div className="image-grid">
            {landscape.map((img) => (
              <ImageTile
                key={img.id}
                img={img}
                kind="featured"
                allImages={images}
                onOpen={onOpenImage}
                isOpen={openImageId === img.id}
              />
            ))}
          </div>
        </>
      )}

      {square.length > 0 && (
        <>
          <div className="panel-section-label">Social</div>
          <div className="image-grid image-grid-square">
            {square.map((img) => (
              <ImageTile
                key={img.id}
                img={img}
                kind="social"
                allImages={images}
                onOpen={onOpenImage}
                isOpen={openImageId === img.id}
              />
            ))}
          </div>
        </>
      )}

      {pressKit.length > 0 && (
        <>
          {/* FEAT-102 / S-412 — viewable, not markable: amplification images,
              never the piece's featured image. */}
          <div className="panel-section-label">Substack press kit</div>
          <div className="image-grid image-grid-square">
            {pressKit.map((img) => (
              <ImageTile
                key={img.id}
                img={img}
                kind="other"
                allImages={images}
                onOpen={onOpenImage}
                isOpen={openImageId === img.id}
              />
            ))}
          </div>
        </>
      )}

      {other.length > 0 && (
        <>
          <div className="panel-section-label">Other</div>
          <div className="image-grid">
            {other.map((img) => (
              <ImageTile
                key={img.id}
                img={img}
                kind="other"
                allImages={images}
                onOpen={onOpenImage}
                isOpen={openImageId === img.id}
              />
            ))}
          </div>
        </>
      )}

      {images.length === 0 && (
        <div className="panel-empty">No images yet. Upload one above.</div>
      )}
    </div>
  );
}

// ---------- Image uploader (S-174 / FEAT-019) ----------

interface FileUploadResponse {
  id: string;
  content_type: string;
  size: number;
  url: string;
}

interface PieceImageCreateBody {
  name: string;
  format: "landscape" | "square" | "portrait";
  style: string;
  file_uuid: string;
}

function ImageUploader({ pieceId }: { pieceId: number }) {
  const { getToken } = useAuth();
  const api = useApi();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sizeWarning, setSizeWarning] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const createImage = useMutation({
    mutationFn: (body: PieceImageCreateBody) =>
      api<unknown>(`/pieces/${pieceId}/images`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["piece-images", pieceId] });
      setUploading(false);
      setError(null);
    },
    onError: (err: Error) => {
      setUploading(false);
      setError(`Failed to register image: ${err.message}`);
    },
  });

  async function handleFile(file: File) {
    setError(null);
    setSizeWarning(null);

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Unsupported file type. Please upload PNG, JPG, WEBP, or GIF.");
      return;
    }

    if (file.size > WARN_SIZE_BYTES) {
      setSizeWarning(
        `File is ${(file.size / 1024 / 1024).toFixed(1)} MB — uploads over 5 MB may be slow or rejected by the server.`
      );
    }

    setUploading(true);
    try {
      const token = await getToken({ template: "ccm-default" });
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE_URL}/files`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        setUploading(false);
        setError(`Upload failed (${res.status}): ${text || "unknown"}`);
        return;
      }
      const data = (await res.json()) as FileUploadResponse;
      // Derive the name from the filename (strip extension).
      const baseName = file.name.replace(/\.[^.]+$/, "");
      createImage.mutate({
        name: baseName,
        format: "landscape",
        style: "stock",
        file_uuid: data.id,
      });
    } catch (err) {
      setUploading(false);
      setError(err instanceof Error ? err.message : "Upload failed");
    }
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    // Reset so re-selecting the same file fires onChange.
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(true);
  }

  function onDragLeave() {
    setIsDragging(false);
  }

  const isPending = uploading || createImage.isPending;

  return (
    <div className="image-uploader" style={{ marginBottom: 16 }}>
      <div
        className={`image-drop-zone ${isDragging ? "is-dragging" : ""} ${isPending ? "is-uploading" : ""}`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        style={{
          border: `2px dashed ${isDragging ? "var(--accent, #6c63ff)" : "var(--rule, #ccc)"}`,
          borderRadius: 6,
          padding: "12px 16px",
          textAlign: "center",
          cursor: isPending ? "not-allowed" : "pointer",
          opacity: isPending ? 0.7 : 1,
          background: isDragging ? "var(--surface-raised, #f5f5f5)" : undefined,
          transition: "border-color 0.15s, background 0.15s",
        }}
        onClick={() => !isPending && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !isPending) {
            inputRef.current?.click();
          }
        }}
        aria-label="Upload image — click or drag and drop"
      >
        {isPending ? (
          <span className="mono" style={{ fontSize: 12 }}>Uploading…</span>
        ) : (
          <span style={{ fontSize: 12, color: "var(--muted-fg)" }}>
            Drop an image here, or{" "}
            <span style={{ color: "var(--accent, #6c63ff)", textDecoration: "underline" }}>
              click to browse
            </span>
            <br />
            <span className="mono" style={{ fontSize: 11 }}>PNG · JPG · WEBP · GIF</span>
          </span>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        style={{ display: "none" }}
        onChange={onFileChange}
        disabled={isPending}
      />
      {sizeWarning && (
        <div style={{ fontSize: 11, color: "var(--warn-fg, #a06000)", marginTop: 4 }}>
          {sizeWarning}
        </div>
      )}
      {error && (
        <div style={{ fontSize: 11, color: "var(--danger, #c33)", marginTop: 4 }}>
          {error}
        </div>
      )}
    </div>
  );
}

function useSetSelectedImage() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      pieceId: number;
      imageId: number;
      kind: "featured" | "social";
      allImageIds: number[];
    }) => {
      const field =
        vars.kind === "featured"
          ? "selected_featured"
          : "selected_social";
      // PATCH /images/:id — issue one request per image so each one
      // ends up at the right value. RLS scopes the writes; the
      // ordering is best-effort consistent for the rows in this piece.
      await Promise.all(
        vars.allImageIds.map((id) =>
          api(`/images/${id}`, {
            method: "PATCH",
            body: JSON.stringify({ [field]: id === vars.imageId }),
          })
        )
      );
      return vars;
    },
    onSuccess: (vars) => {
      void qc.invalidateQueries({
        queryKey: ["piece-images", vars.pieceId],
      });
    },
  });
}

function ImageTile({
  img,
  kind,
  allImages,
  onOpen,
  isOpen = false,
}: {
  img: PieceImage;
  kind: "featured" | "social" | "other";
  allImages: PieceImage[];
  onOpen?: (id: number) => void;
  isOpen?: boolean;
}) {
  const setSelected = useSetSelectedImage();
  // FEAT-108 / S-438 — choosing the featured or social image is an edit.
  const { canEdit } = useRole();
  const markable = kind !== "other" && canEdit;
  const selected =
    (kind === "featured" && img.selected_featured) ||
    (kind === "social" && img.selected_social);
  const url = r2AssetUrl(img.image, { width: 480, fit: "cover" });

  const mark = () => {
    if (!markable || setSelected.isPending) return;
    setSelected.mutate({
      pieceId: img.article,
      imageId: img.id,
      kind,
      allImageIds: allImages.map((i) => i.id),
    });
  };

  // Without an onOpen the tile behaves as it always did: the whole tile is
  // the mark-as-featured/social control.
  if (!onOpen) {
    const Wrapper: React.ElementType = markable ? "button" : "div";
    return (
      <Wrapper
        className={`image-tile ${selected ? "is-selected" : ""} ${
          setSelected.isPending ? "is-saving" : ""
        }`}
        onClick={mark}
        type={markable ? "button" : undefined}
        title={
          !markable ? undefined : selected ? "Selected" : `Mark as ${kind}`
        }
      >
        <ImageTileFace img={img} url={url} />
        {selected && <span className="image-tile-badge">Selected</span>}
      </Wrapper>
    );
  }

  return (
    <div
      className={`image-tile ${selected ? "is-selected" : ""} ${
        isOpen ? "is-open" : ""
      } ${setSelected.isPending ? "is-saving" : ""}`}
    >
      <button
        type="button"
        className="image-tile-open"
        onClick={() => onOpen(img.id)}
        title="View full size"
        aria-pressed={isOpen}
      >
        <ImageTileFace img={img} url={url} />
      </button>
      {markable && (
        <button
          type="button"
          className="image-tile-select"
          onClick={mark}
          disabled={setSelected.isPending}
        >
          {selected ? "Selected" : `Mark as ${kind}`}
        </button>
      )}
      {selected && <span className="image-tile-badge">Selected</span>}
    </div>
  );
}

function ImageTileFace({ img, url }: { img: PieceImage; url: string | null }) {
  return (
    <>
      {url ? (
        <img src={url} alt={img.name || ""} loading="lazy" />
      ) : (
        <div className="image-tile-placeholder">no preview</div>
      )}
      <div className="image-tile-meta">
        <span className="image-tile-name">
          {img.name || formatContentType(img.style)}
        </span>
        {img.style && <span className="image-tile-attr mono">{img.style}</span>}
      </div>
    </>
  );
}
